import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Alert, Box, Typography, useTheme } from '@mui/material'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import NotesOutlined from '@mui/icons-material/NotesOutlined'
import { ErrorBoundary } from '@/infra/error'
import { useSessionStateContext } from '@/features/events/conversation/state'
import type { ClaudeSession, UserInputDraft } from '@/features/events/conversation/state/types'
import { getLatestConversationTurn, hasAnyDrafts } from '@/features/events/conversation/state/types'
import { UserInputDialog } from '@/features/events/conversation/dialogs'
import { useCommandSubmitter } from '@/features/events/conversation/controls'
import { SessionTree } from '@/features/events/conversation/tree'
import type {
  TreeConfig,
  ContentContainerConfig,
  TreeSortOrder,
} from '@/features/events/conversation/tree'
import { OrderByControl } from '@/common/components'
import type { SortOption } from '@/common/components'
import { useSortOrderState } from '@/common/hooks'
import { ConnectedHostStatusIcon, useStatusContext } from '@/features/status'
import { isLiveSessionStatus } from '@/infra/sse/status'
import { DRAFT_DIALOG_CONFIG } from '../shared'
import { useSubmitUserInputDraftHandler } from '@/features/events/conversation/drafts'
import { resolveSessionAgentMode } from '@/features/events/shared/utils/agentModeUtils'
import { SessionDraftsOrchestrator } from './components'

function renderHostIcon(hostname: string) {
  return <ConnectedHostStatusIcon hostname={hostname} />
}

const DRAFTS_SORT_STORAGE_KEY = 'drafts-sort-order'

const DRAFTS_SORT_OPTIONS: readonly SortOption<TreeSortOrder>[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'alpha-asc', label: 'A \u2192 Z' },
  { value: 'alpha-desc', label: 'Z \u2192 A' },
] as const

const VALID_DRAFTS_SORT_VALUES: readonly TreeSortOrder[] = [
  'newest',
  'oldest',
  'alpha-asc',
  'alpha-desc',
]

interface SelectedAnswerDraft {
  readonly questionHeader: string
  readonly sessionId: string
}

export const DraftsPage: FC = () => {
  const theme = useTheme()
  const { sessions, recordCommand, createNewTurn } = useSessionStateContext()

  const [sortOrder, setSortOrder] = useSortOrderState<TreeSortOrder>(
    DRAFTS_SORT_STORAGE_KEY,
    'newest',
    VALID_DRAFTS_SORT_VALUES,
  )
  const [selectedDraft, setSelectedDraft] = useState<UserInputDraft | null>(null)
  const [selectedSession, setSelectedSession] = useState<ClaudeSession | null>(null)
  const [selectedAnswerDraft, setSelectedAnswerDraft] = useState<SelectedAnswerDraft | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [answerDialogOpen, setAnswerDialogOpen] = useState(false)

  const { getSessionStatus, isLoading: isStatusLoading } = useStatusContext()

  const { liveSessions, endedDraftCount } = useMemo(() => {
    let endedCount = 0
    const live = sessions.filter((s) => {
      if (!hasAnyDrafts(s)) return false
      const status = getSessionStatus(s.sessionId)
      // Sessions with unknown status (null) are treated as live — matches Events page bucketing policy
      if (isStatusLoading || status === null) return true
      if (!isLiveSessionStatus(status)) {
        endedCount++
        return false
      }
      return true
    })
    return { liveSessions: live, endedDraftCount: endedCount }
  }, [sessions, getSessionStatus, isStatusLoading])

  const draftsTreeConfig: TreeConfig = useMemo(
    () => ({
      icons: {
        host: renderHostIcon,
        project: <FolderOutlined fontSize="small" />,
        session: <NotesOutlined fontSize="small" />,
      },
      expandTogglePosition: 'left',
      persistExpansion: false,
      defaultExpanded: true,
      showCounts: false,
      sortOrder,
    }),
    [sortOrder],
  )

  const draftsContentContainerConfig: ContentContainerConfig = useMemo(
    () => ({
      borderColor:
        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(62, 39, 35, 0.12)',
    }),
    [theme.palette.mode],
  )

  const conversationTurn = useMemo(() => {
    if (!selectedSession) return null
    return getLatestConversationTurn(selectedSession)
  }, [selectedSession])

  const handleSubmitSuccess = useCallback(() => {
    setDialogOpen(false)
    setSelectedDraft(null)
    setSelectedSession(null)
  }, [])

  const submitter = useCommandSubmitter({
    session: selectedSession,
    conversationTurn,
    recordCommand,
    createNewTurn,
    onSuccess: handleSubmitSuccess,
  })

  const handleDraftClick = useCallback((draft: UserInputDraft, session: ClaudeSession) => {
    setSelectedDraft(draft)
    setSelectedSession(session)
    setDialogOpen(true)
  }, [])

  const handleAnswerDraftClick = useCallback((questionHeader: string, session: ClaudeSession) => {
    const answer = session.runtime.memory.userAnswersDraft?.answers[questionHeader]
    if (answer) {
      setSelectedAnswerDraft({ questionHeader, sessionId: session.sessionId })
      setAnswerDialogOpen(true)
    }
  }, [])

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false)
    setSelectedDraft(null)
    setSelectedSession(null)
  }, [])

  const handleAnswerDialogClose = useCallback(() => {
    setAnswerDialogOpen(false)
    setSelectedAnswerDraft(null)
  }, [])

  const { handleSubmit } = useSubmitUserInputDraftHandler({
    session: selectedSession,
    selectedDraft,
    submitter,
  })

  const handleAnswerSubmit = useCallback(async (): Promise<boolean> => {
    setAnswerDialogOpen(false)
    setSelectedAnswerDraft(null)
    return true
  }, [])

  const dialogConfig = selectedDraft ? DRAFT_DIALOG_CONFIG[selectedDraft.type] : null
  const answerDialogConfig = DRAFT_DIALOG_CONFIG['custom_answer']

  const selectedAnswerSession = useMemo(() => {
    if (!selectedAnswerDraft) return null
    return sessions.find((s) => s.sessionId === selectedAnswerDraft.sessionId) ?? null
  }, [sessions, selectedAnswerDraft])

  const selectedAnswerDraftData = useMemo(() => {
    if (!selectedAnswerSession || !selectedAnswerDraft) return null
    return (
      selectedAnswerSession.runtime.memory.userAnswersDraft?.answers[
        selectedAnswerDraft.questionHeader
      ] ?? null
    )
  }, [selectedAnswerSession, selectedAnswerDraft])

  const currentAgentMode = selectedSession ? resolveSessionAgentMode(selectedSession) : null
  const answerCurrentAgentMode = selectedAnswerSession
    ? resolveSessionAgentMode(selectedAnswerSession)
    : null

  return (
    <Box sx={contentWrapperSx}>
      <Box sx={headerSx}>
        <Box component="h1" sx={titleSx}>
          Drafts
        </Box>
      </Box>

      {liveSessions.length > 0 && (
        <Box sx={controlsRowSx}>
          <OrderByControl<TreeSortOrder>
            options={DRAFTS_SORT_OPTIONS}
            value={sortOrder}
            onChange={setSortOrder}
          />
        </Box>
      )}

      {endedDraftCount > 0 && (
        <Alert severity="info" sx={endedDraftsBannerSx}>
          {endedDraftCount} {endedDraftCount === 1 ? 'draft' : 'drafts'} for ended sessions
          {endedDraftCount === 1 ? ' was' : ' were'} hidden.
        </Alert>
      )}

      <Box sx={mainAreaSx}>
        <ErrorBoundary>
          {liveSessions.length === 0 ? (
            <EmptyState />
          ) : (
            <SessionTree
              sessions={liveSessions}
              config={draftsTreeConfig}
              contentContainerConfig={draftsContentContainerConfig}
              renderSessionOrchestrator={(session) => (
                <SessionDraftsOrchestrator
                  session={session}
                  onDraftSelect={handleDraftClick}
                  onAnswerDraftSelect={handleAnswerDraftClick}
                />
              )}
              emptyMessage="No drafts available"
            />
          )}
        </ErrorBoundary>
      </Box>

      {selectedDraft && selectedSession && dialogConfig && (
        <UserInputDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSubmit={handleSubmit}
          session={selectedSession}
          isSubmitting={submitter.isSubmitting}
          error={submitter.error}
          draftType={selectedDraft.type}
          refEventId={selectedDraft.refEventId}
          title={dialogConfig.title}
          placeholder={dialogConfig.placeholder}
          description={dialogConfig.description}
          submitButtonLabel={dialogConfig.submitButtonLabel}
          submitButtonLoadingLabel={dialogConfig.submitButtonLoadingLabel}
          initialValue={selectedDraft.content}
          currentAgentMode={currentAgentMode}
        />
      )}

      {selectedAnswerDraft && selectedAnswerSession && selectedAnswerDraftData && (
        <UserInputDialog
          open={answerDialogOpen}
          onClose={handleAnswerDialogClose}
          onSubmit={handleAnswerSubmit}
          session={selectedAnswerSession}
          isSubmitting={false}
          error={null}
          draftType="custom_answer"
          refEventId={null}
          title={answerDialogConfig.title}
          placeholder={answerDialogConfig.placeholder}
          description={answerDialogConfig.description}
          submitButtonLabel={answerDialogConfig.submitButtonLabel}
          submitButtonLoadingLabel={answerDialogConfig.submitButtonLoadingLabel}
          initialValue={selectedAnswerDraftData.customAnswer ?? ''}
          questionHeader={selectedAnswerDraft.questionHeader}
          eventId={selectedAnswerSession.runtime.memory.userAnswersDraft?.eventId}
          deleteOnSubmit={false}
          currentAgentMode={answerCurrentAgentMode}
        />
      )}
    </Box>
  )
}

const EmptyState: FC = () => (
  <Box sx={emptyStateSx}>
    <Typography variant="body2" color="text.secondary">
      No drafts yet
    </Typography>
    <Typography variant="caption" color="text.disabled" sx={emptyStateHintSx}>
      Drafts are automatically saved when you close a prompt dialog or click the save icon.
    </Typography>
    <Typography variant="caption" color="text.disabled">
      Cleared when you reply to the Agent.
    </Typography>
  </Box>
)

const contentWrapperSx: SxProps<Theme> = {
  padding: 4,
  paddingTop: 6,
}

const headerSx: SxProps<Theme> = {
  textAlign: 'center',
  mb: 6,
}

const titleSx: SxProps<Theme> = {
  fontSize: '1.75rem',
  fontWeight: 400,
  color: 'text.primary',
  mb: 1,
  letterSpacing: '0.01em',
}

const mainAreaSx: SxProps<Theme> = {
  maxWidth: '1200px',
  mx: 'auto',
}

const controlsRowSx: SxProps<Theme> = {
  maxWidth: '1200px',
  mx: 'auto',
  display: 'flex',
  justifyContent: 'flex-end',
  mb: 2,
}

const endedDraftsBannerSx: SxProps<Theme> = {
  maxWidth: '1200px',
  mx: 'auto',
  mb: 2,
}

const emptyStateSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  py: 8,
}

const emptyStateHintSx: SxProps<Theme> = {
  mt: 1,
}
